import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  IconButton,
  Tooltip,
  Checkbox,
  Typography,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import DataTableMobile from './DataTableMobile';
import { DataTableProps } from '@/models/ui';
import './DataTable.css';

function DataTable<T extends Record<string, any>>({
  columns,
  data = [],
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onSort,
  actions,
  selectable = false,
  onSelectionChange,
  selectedIds = [],
  idField = '_id' as keyof T,
  loading = false,
  searchPlaceholder = 'Search...',
  showExport = false,
  onExport,
  title,
  subtitle,
  emptyMessage = 'No data available',
  error,
  dense = false,
  stickyHeader = true,
  maxHeight,
}: DataTableProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [sortField, setSortField] = useState<keyof T | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchValue, setSearchValue] = useState('');

  const safeData = useMemo(() => data || [], [data]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    onSearch?.(value);
  };

  const clearSearch = () => {
    setSearchValue('');
    onSearch?.('');
  };

  const handleSort = (field: keyof T) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    onSort?.(field, newOrder);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onSelectionChange?.(safeData.map(row => row[idField] as string));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter(sid => sid !== id)
      : [...selectedIds, id];
    onSelectionChange?.(next);
  };

  const isAllSelected = safeData.length > 0 && selectedIds.length === safeData.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < safeData.length;

  const pagination = (
    <TablePagination
      rowsPerPageOptions={[10, 25, 50, 100]}
      component="div"
      count={total}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={(_, newPage) => onPageChange(newPage)}
      onRowsPerPageChange={e => onRowsPerPageChange(parseInt(e.target.value, 10))}
      className="dt-pagination"
      labelRowsPerPage="Rows:"
    />
  );

  const searchBar = onSearch ? (
    <Box className="dt-search-bar">
      <TextField
        size="small"
        value={searchValue}
        onChange={handleSearchChange}
        placeholder={searchPlaceholder}
        className="dt-search-field"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: searchValue ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={clearSearch} edge="end" className="dt-search-clear">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  ) : null;

  if (isMobile) {
    return (
      <Box className="dt-mobile-wrapper">
        {searchBar}
        <DataTableMobile
          data={safeData}
          columns={columns}
          actions={actions}
          loading={loading}
          emptyMessage={emptyMessage}
          onActionClick={(action, item) => action.onClick(item)}
        />
        {pagination}
      </Box>
    );
  }

  const actionsMinWidth = actions ? Math.max(actions.length * 36 + 16, 100) : 80;

  return (
    <Paper className="dt-container" elevation={0}>
      {searchBar}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          {error}
        </Alert>
      )}

      {pagination}

      <TableContainer className="dt-table-container" style={maxHeight ? { maxHeight } : undefined}>
        <Table stickyHeader={stickyHeader} size="small">
          <TableHead>
            <TableRow className="dt-header-row">
              {selectable && (
                <TableCell
                  padding="checkbox"
                  className="dt-col-sticky-left dt-col-checkbox"
                  style={{ backgroundColor: theme.palette.background.paper }}
                >
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
              )}

              {columns.map((column, index) => (
                <TableCell
                  key={column.id as string}
                  align={column.align}
                  className={column.sortable ? 'dt-sortable' : ''}
                  style={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                  }}
                  onClick={() => column.sortable && handleSort(column.id)}
                >
                  <Box className="dt-header-cell">
                    <span>{column.label}</span>
                    {column.sortable && sortField === column.id && (
                      sortOrder === 'asc'
                        ? <KeyboardArrowUp className="dt-sort-arrow" />
                        : <KeyboardArrowDown className="dt-sort-arrow" />
                    )}
                  </Box>
                </TableCell>
              ))}

              {actions && actions.length > 0 && (
                <TableCell
                  align="right"
                  className="dt-col-sticky-right dt-col-actions"
                  style={{
                    minWidth: actionsMinWidth,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: Math.min(rowsPerPage, 8) }).map((_, i) => (
                <TableRow key={`sk-${i}`} className="dt-row">
                  {selectable && (
                    <TableCell padding="checkbox" className="dt-col-sticky-left dt-col-checkbox"
                      style={{ backgroundColor: theme.palette.background.paper }}>
                      <Skeleton variant="rectangular" width={18} height={18} />
                    </TableCell>
                  )}
                  {columns.map(col => (
                    <TableCell key={col.id as string} align={col.align}>
                      <Skeleton variant="text" width="70%" />
                    </TableCell>
                  ))}
                  {actions && actions.length > 0 && (
                    <TableCell align="right" className="dt-col-sticky-right dt-col-actions"
                      style={{ backgroundColor: theme.palette.background.paper }}>
                      <Box className="dt-row-actions">
                        <Skeleton variant="circular" width={26} height={26} />
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : safeData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              safeData.map(row => {
                const isSelected = selectedIds.includes(row[idField] as string);
                return (
                  <TableRow
                    hover
                    key={row[idField] as string}
                    selected={isSelected}
                    className="dt-row"
                  >
                    {selectable && (
                      <TableCell
                        padding="checkbox"
                        className="dt-col-sticky-left dt-col-checkbox"
                        style={{
                          backgroundColor: isSelected
                            ? theme.palette.action.selected
                            : theme.palette.background.paper,
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectRow(row[idField] as string)}
                          size="small"
                        />
                      </TableCell>
                    )}

                    {columns.map(column => (
                      <TableCell
                        key={column.id as string}
                        align={column.align}
                        style={{
                          maxWidth: column.ellipsis ? (column.maxWidth || 220) : undefined,
                          overflow: column.ellipsis ? 'hidden' : undefined,
                          textOverflow: column.ellipsis ? 'ellipsis' : undefined,
                          whiteSpace: column.ellipsis ? 'nowrap' : undefined,
                        }}
                        title={column.ellipsis ? String(row[column.id] ?? '') : undefined}
                      >
                        {column.format ? column.format(row[column.id], row) : row[column.id]}
                      </TableCell>
                    ))}

                    {actions && actions.length > 0 && (
                      <TableCell
                        align="right"
                        className="dt-col-sticky-right dt-col-actions"
                        style={{
                          backgroundColor: isSelected
                            ? theme.palette.action.selected
                            : theme.palette.background.paper,
                        }}
                      >
                        <Box className="dt-row-actions">
                          {actions.map((action, i) => (
                            <Tooltip key={i} title={action.label} arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => action.onClick(row)}
                                  disabled={action.disabled?.(row)}
                                  className={`dt-row-btn ${action.color || ''}`}
                                >
                                  {action.icon}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

    </Paper>
  );
}

export default DataTable;
